import { crc16 } from '../shared/crc';
import { BASE_EPOCH } from './constants';
import type { DecodeResult, EmrgPayload, EmrgResource } from './types';
import { detectTier, hexToBytes } from './binary';
import { wireErrors } from './protocol';

const SENTINEL_GPS = 0x7FFFFFFF;

function readAscii(bytes: Uint8Array, offset: number, length: number): string {
  let s = '';
  for (let i = 0; i < length; i++) {
    const c = bytes[offset + i] ?? 0;
    if (c === 0) break;
    s += String.fromCharCode(c & 0x7F);
  }
  return s;
}

export function readText(bytes: Uint8Array, offset: number, length: number, encoding: number): string {
  if (encoding === 0 || encoding === 3) return '';
  const field = bytes.subarray(offset, offset + length);
  if (encoding === 2) return unpackText7bit(field, field.length);
  if (encoding === 4) {
    const end = field.indexOf(0);
    return new TextDecoder('utf-8', { fatal: true, ignoreBOM: true }).decode(end < 0 ? field : field.subarray(0, end));
  }
  if (encoding !== 1) throw new Error(`Unknown text encoding: ${encoding}`);
  return readAscii(bytes, offset, length);
}

/**
 * Inverse of `packText7bit`. Unpacks GSM-style 7-bit packed bytes back to ASCII.
 * Used internally by `decode()` when `message.encoding === 2`.
 *
 * @param bytes - Source buffer
 * @param byteCount - Number of bytes to consume from the buffer
 * @returns The decoded ASCII string (up to `floor(byteCount * 8 / 7)` chars)
 */
export function unpackText7bit(bytes: Uint8Array, byteCount: number): string {
  // Inverse of packText7bit. Returns up to floor(byteCount * 8 / 7) chars.
  let bitBuf = 0;
  let bitCount = 0;
  let s = '';
  for (let i = 0; i < byteCount; i++) {
    bitBuf |= (bytes[i]! & 0xFF) << bitCount;
    bitCount += 8;
    while (bitCount >= 7) {
      const c = bitBuf & 0x7F;
      if (c === 0) return s;
      s += String.fromCharCode(c);
      bitBuf >>= 7;
      bitCount -= 7;
    }
  }
  return s;
}

function emptyPayload(): EmrgPayload {
  return {
    header: { schemaId: 'EMRG', version: [1, 0], subtype: 0 },
    envelope: { sequence: 0, timestamp: new Date(0), priority: 0, ttl: 0, deviceId: 0 },
    location: { hasGps: false, hasAltitude: false, highPrecision: false, inMotion: false },
    event: { category: '00', severity: 1, status: '00', radius: 0, responseFlags: 0 },
    resources: [],
    message: { encoding: 0, ref: 0, text: '' },
  };
}

function readResources(view: DataView, offset: number, slots: number, count: number): EmrgResource[] {
  const out: EmrgResource[] = [];
  const n = Math.min(count, slots);
  for (let i = 0; i < n; i++) {
    const type = view.getUint8(offset + i * 2);
    const status = view.getUint8(offset + i * 2 + 1);
    if (type === 0 && status === 0) continue;
    out.push({
      type: type.toString(16).padStart(2, '0').toUpperCase(),
      status,
    });
  }
  return out;
}

/**
 * Decode an EMRG hex payload into a structured object.
 *
 * Parses the schema header, envelope, location, event, resources, and message
 * blocks. The CRC is verified and reflected in the `valid` field; an invalid
 * CRC does not throw — callers should check `result.valid`.
 *
 * @param hex - Hex string (with or without whitespace, with or without 0x prefix)
 * @returns Structured payload, detected tier, validity flag, and any errors
 * @throws If the hex string is malformed (odd length, non-hex chars)
 */
export function decode(hex: string): DecodeResult {
  const errors: string[] = [];
  const payload = emptyPayload();
  let tier: 0 | 1 | 2 = 0;

  let bytes: Uint8Array;
  try {
    bytes = hexToBytes(hex);
  } catch (e) {
    errors.push(`Invalid hex: ${(e as Error).message}`);
    return { payload, tier, valid: false, errors };
  }

  try {
    tier = detectTier(bytes);
  } catch (e) {
    errors.push((e as Error).message);
    return { payload, tier, valid: false, errors };
  }

  // Verify CRC (continue even on mismatch).
  if (bytes.length >= 2) {
    const view0 = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
    const expected = view0.getUint16(bytes.length - 2, false);
    const actual = crc16(bytes.subarray(0, bytes.length - 2));
    if (expected !== actual) {
      errors.push(`CRC mismatch: expected 0x${expected.toString(16)}, got 0x${actual.toString(16)}`);
    }
  }

  const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
  errors.push(...wireErrors(bytes, tier));

  try {
    if (tier === 0) {
      const subtype = view.getUint8(2);
      const minOffset = (view.getUint8(3) << 16) | (view.getUint8(4) << 8) | view.getUint8(5);
      const tsSec = BASE_EPOCH + minOffset * 60;
      const deviceId = view.getUint16(6, false);
      const pc = view.getUint8(8);
      const priority = (pc >> 4) & 0x0F;
      const ttl = pc & 0x0F;
      const locFlags = view.getUint8(9);
      const latRaw = view.getInt32(10, false);
      const lngRaw = view.getInt32(14, false);
      const sevStatus = view.getUint8(18);
      const severity = ((sevStatus >> 4) & 0x0F) as 1 | 2 | 3 | 4 | 5;
      const statusLow = sevStatus & 0x0F;
      const radiusByte = view.getUint8(19);
      const radius = (radiusByte >> 4) & 0x0F;
      const responseFlags = view.getUint16(20, false);
      const msgRef = view.getUint16(22, false);
      const text = readAscii(bytes, 24, 25);

      payload.header = { schemaId: 'EMRG', version: [1, 0], subtype };
      payload.envelope = { sequence: 0, timestamp: new Date(tsSec * 1000), priority, ttl, deviceId };
      payload.location = {
        hasGps: (locFlags & 0x80) !== 0,
        hasAltitude: (locFlags & 0x40) !== 0,
        highPrecision: (locFlags & 0x20) !== 0,
        inMotion: (locFlags & 0x10) !== 0,
      };
      if (latRaw !== SENTINEL_GPS) payload.location.lat = latRaw / 1_000_000;
      if (lngRaw !== SENTINEL_GPS) payload.location.lng = lngRaw / 1_000_000;
      payload.event = {
        category: subtype.toString(16).padStart(2, '0').toUpperCase(),
        severity,
        status: statusLow.toString(16).padStart(2, '0').toUpperCase(),
        radius,
        responseFlags,
      };
      payload.resources = [];
      payload.message = { encoding: 1, ref: msgRef, text };
    } else {
      // Tier 1 + Tier 2 share prefix.
      const versionByte = view.getUint8(5);
      const subtype = view.getUint8(6);
      const sequence = view.getUint16(7, false);
      const tsOffset = view.getUint32(9, false);
      const pc = view.getUint8(13);
      const priority = (pc >> 4) & 0x0F;
      const ttl = pc & 0x0F;
      const deviceId = (view.getUint8(14) << 16) | (view.getUint8(15) << 8) | view.getUint8(16);
      const locFlags = view.getUint8(17);
      const latRaw = view.getInt32(18, false);
      const lngRaw = view.getInt32(22, false);
      const category = view.getUint8(26);
      const severity = view.getUint8(27) as 1 | 2 | 3 | 4 | 5;
      const statusByte = view.getUint8(28);
      const radius = view.getUint8(29);
      const responseFlags = view.getUint16(30, false);
      const rcount = view.getUint8(32);

      payload.header = {
        schemaId: 'EMRG',
        version: [(versionByte >> 4) & 0x0F, versionByte & 0x0F],
        subtype,
      };
      payload.envelope = {
        sequence,
        timestamp: new Date((BASE_EPOCH + tsOffset) * 1000),
        priority,
        ttl,
        deviceId,
      };
      payload.location = {
        hasGps: (locFlags & 0x80) !== 0,
        hasAltitude: (locFlags & 0x40) !== 0,
        highPrecision: (locFlags & 0x20) !== 0,
        inMotion: (locFlags & 0x10) !== 0,
      };
      if (latRaw !== SENTINEL_GPS) payload.location.lat = latRaw / 1_000_000;
      if (lngRaw !== SENTINEL_GPS) payload.location.lng = lngRaw / 1_000_000;
      payload.event = {
        category: category.toString(16).padStart(2, '0').toUpperCase(),
        severity,
        status: statusByte.toString(16).padStart(2, '0').toUpperCase(),
        radius,
        responseFlags,
      };

      if (tier === 1) {
        payload.resources = readResources(view, 33, 5, rcount);
        const enc = view.getUint8(43);
        const ref = view.getUint16(44, false);
        const text = readText(bytes, 46, 67, enc);
        payload.message = { encoding: enc, ref, text };
      } else {
        payload.resources = readResources(view, 33, 10, rcount);
        const enc = view.getUint8(53);
        const ref = view.getUint16(54, false);
        const text = readText(bytes, 56, 164, enc);
        payload.message = { encoding: enc, ref, text };
      }
    }
  } catch (e) {
    errors.push(`Decode error: ${(e as Error).message}`);
  }

  return { payload, tier, valid: errors.length === 0, errors };
}
